import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp, Eye } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';

import { StatusBadge } from './StatusBadge';

import type { PaginationMeta, Submission } from '@/types/submission.types';

type SortField = 'applicationId' | 'lastName' | 'stage' | 'submittedAt';

interface SubmissionsTableProps {
  submissions: Submission[];
  isLoading: boolean;
  onRowClick: (submission: Submission) => void;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  onSort: (field: string) => void;
  meta: PaginationMeta | null;
  onPageChange: (page: number) => void;
}

const SKELETON_ROWS = 5;

export function SubmissionsTable({
  submissions,
  isLoading,
  onRowClick,
  sortBy,
  sortOrder,
  onSort,
  meta,
  onPageChange,
}: SubmissionsTableProps) {
  const { t } = useTranslation();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  function handleSelectAll() {
    if (selectedIds.size === submissions.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(submissions.map((s) => s.id)));
    }
  }

  function handleSelectRow(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  function renderSortIcon(field: SortField) {
    if (sortBy !== field) {
      return <ChevronDown size={14} className='ml-1 text-gray-300' />;
    }
    return sortOrder === 'asc' ? <ChevronUp size={14} className='ml-1' /> : <ChevronDown size={14} className='ml-1' />;
  }

  const isAllSelected = submissions.length > 0 && selectedIds.size === submissions.length;

  if (isLoading) {
    return (
      <div className='bg-white shadow rounded-lg overflow-hidden border border-gray-200'>
        <table className='w-full table-fixed divide-y divide-gray-200'>
          <thead className='bg-gray-50'>
            <tr>
              {Array.from({ length: 9 }).map((_, i) => (
                <th key={i} className='px-3 py-3'>
                  <div className='h-4 bg-gray-200 rounded animate-pulse w-16' />
                </th>
              ))}
            </tr>
          </thead>
          <tbody className='divide-y divide-gray-200'>
            {Array.from({ length: SKELETON_ROWS }).map((_, rowIndex) => (
              <tr key={rowIndex}>
                {Array.from({ length: 9 }).map((_col, colIndex) => (
                  <td key={colIndex} className='px-3 py-3'>
                    <div className='h-4 bg-gray-100 rounded animate-pulse' />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (submissions.length === 0) {
    return (
      <div className='bg-white shadow rounded-lg border border-gray-200 p-12 text-center'>
        <p className='text-gray-500'>{t('submissions.noResults')}</p>
      </div>
    );
  }

  return (
    <div className='bg-white shadow rounded-lg overflow-hidden border border-gray-200'>
      {selectedIds.size > 0 && (
        <div className='bg-primary/5 px-6 py-2 text-sm text-primary border-b'>
          {t('submissions.selected', { count: selectedIds.size })}
        </div>
      )}
      <table className='w-full table-fixed divide-y divide-gray-200'>
        <thead className='bg-gray-50'>
          <tr>
            <th className='w-10 px-3 py-3 text-left'>
              <input
                type='checkbox'
                checked={isAllSelected}
                onChange={handleSelectAll}
                className='rounded border-gray-300'
                aria-label={t('submissions.columns.select')}
              />
            </th>
            <th
              className='w-[12%] px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none'
              onClick={() => onSort('applicationId')}>
              <span className='inline-flex items-center'>
                {t('submissions.columns.applicationId')}
                {renderSortIcon('applicationId')}
              </span>
            </th>
            <th
              className='w-[16%] px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none'
              onClick={() => onSort('lastName')}>
              <span className='inline-flex items-center'>
                {t('submissions.columns.applicant')}
                {renderSortIcon('lastName')}
              </span>
            </th>
            <th className='w-[15%] px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
              {t('submissions.columns.jobPosting')}
            </th>
            <th className='w-[12%] px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
              {t('submissions.columns.roles')}
            </th>
            <th className='w-[13%] px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
              {t('submissions.columns.sites')}
            </th>
            <th
              className='w-[11%] px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none'
              onClick={() => onSort('stage')}>
              <span className='inline-flex items-center'>
                {t('submissions.columns.stage')}
                {renderSortIcon('stage')}
              </span>
            </th>
            <th
              className='w-[11%] px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none'
              onClick={() => onSort('submittedAt')}>
              <span className='inline-flex items-center'>
                {t('submissions.columns.submittedAt')}
                {renderSortIcon('submittedAt')}
              </span>
            </th>
            <th className='w-16 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
              {t('submissions.columns.actions')}
            </th>
          </tr>
        </thead>
        <tbody className='bg-white divide-y divide-gray-200'>
          {submissions.map((submission) => {
            const isSelected = selectedIds.has(submission.id);
            const firstRole = submission.roles[0] ?? '';
            const extraRolesCount = submission.roles.length - 1;

            return (
              <tr
                key={submission.id}
                className={cn('hover:bg-blue-50 cursor-pointer transition-colors', isSelected && 'bg-primary/5')}
                onClick={() => onRowClick(submission)}>
                <td className='px-3 py-3'>
                  <input
                    type='checkbox'
                    checked={isSelected}
                    onChange={() => handleSelectRow(submission.id)}
                    onClick={(e) => e.stopPropagation()}
                    className='rounded border-gray-300'
                  />
                </td>
                <td className='px-3 py-3'>
                  <span className='text-sm font-mono text-gray-900 truncate block'>{submission.applicationId}</span>
                </td>
                <td className='px-3 py-3'>
                  <div className='flex items-center min-w-0'>
                    <div className='flex-shrink-0 h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold text-xs'>
                      {submission.firstName[0]}
                      {submission.lastName[0]}
                    </div>
                    <div className='ml-3 min-w-0'>
                      <div className='text-sm font-medium text-gray-900 truncate'>
                        {submission.firstName} {submission.lastName}
                      </div>
                      <div className='text-xs text-gray-500 truncate'>{submission.email}</div>
                    </div>
                  </div>
                </td>
                <td className='px-3 py-3'>
                  {submission.jobPostingNumber ? (
                    <div className='min-w-0'>
                      <div className='text-sm font-mono text-gray-900 truncate'>{submission.jobPostingNumber}</div>
                      {submission.jobPostingTitle && (
                        <div className='text-xs text-gray-500 truncate'>{submission.jobPostingTitle}</div>
                      )}
                    </div>
                  ) : (
                    <span className='text-sm text-gray-400'>—</span>
                  )}
                </td>
                <td className='px-3 py-3'>
                  <span className='text-sm font-medium text-gray-900'>{firstRole}</span>
                  {extraRolesCount > 0 && (
                    <span className='ml-1 inline-flex items-center justify-center h-5 min-w-[20px] px-1 rounded-full bg-gray-100 text-xs font-medium text-gray-600'>
                      +{extraRolesCount}
                    </span>
                  )}
                </td>
                <td className='px-3 py-3 text-sm text-gray-500'>{submission.locations.join(', ')}</td>
                <td className='px-3 py-3'>
                  <StatusBadge stage={submission.stage} />
                </td>
                <td className='px-3 py-3 text-sm text-gray-500 whitespace-nowrap'>
                  {new Date(submission.submittedAt).toLocaleDateString()}
                </td>
                <td className='px-3 py-3'>
                  <button
                    type='button'
                    className='inline-flex items-center gap-1 text-sm text-primary hover:text-primary/80 font-medium'
                    onClick={(e) => {
                      e.stopPropagation();
                      onRowClick(submission);
                    }}>
                    <Eye size={14} />
                    {t('submissions.view')}
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {meta && meta.totalPages > 1 && (
        <div className='flex items-center justify-between border-t border-gray-200 bg-gray-50 px-6 py-3'>
          <p className='text-sm text-gray-700'>
            {t('submissions.pagination.showing', {
              from: (meta.page - 1) * meta.limit + 1,
              to: Math.min(meta.page * meta.limit, meta.total),
              total: meta.total,
            })}
          </p>
          <div className='flex items-center gap-2'>
            <button
              type='button'
              disabled={meta.page <= 1}
              onClick={() => onPageChange(meta.page - 1)}
              className='inline-flex items-center rounded-md border border-gray-300 bg-white px-2 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'>
              <ChevronLeft size={16} />
            </button>
            <span className='text-sm text-gray-700'>
              {t('submissions.pagination.page', { current: meta.page, total: meta.totalPages })}
            </span>
            <button
              type='button'
              disabled={meta.page >= meta.totalPages}
              onClick={() => onPageChange(meta.page + 1)}
              className='inline-flex items-center rounded-md border border-gray-300 bg-white px-2 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'>
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
